import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import { useTheme } from "../contexts/ThemeContext";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { isDark, toggleTheme } = useTheme();

  return (
    <nav className="bg-white dark:bg-dark-800 shadow-lg fixed w-full z-50 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
        <div className="flex justify-between items-center h-14 sm:h-16">
          {/* Logo */}
          <div className="flex items-center">
            <NavLink to="/" className="flex items-center">
              <img 
                src="/jk_logo.png" 
                alt="Jnanakshi Solutions Logo" 
                className="h-10 sm:h-12 md:h-14 lg:h-16 w-auto"
              />
              <div className="hidden xs:block -ml-6">
                <div className="text-lg sm:text-xl md:text-2xl font-bold text-jnanakshi-500 dark:text-jnanakshi-400 leading-tight">
                  Jnanakshi Solutions
                </div>
                <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 italic text-left">
                  The Eye of Knowledge
                </div>
              </div>
            </NavLink>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-4 lg:space-x-6 xl:space-x-8">
            <NavLink
              to="/"
              className={({ isActive }) =>
                `text-gray-700 dark:text-gray-300 hover:text-jnanakshi-500 dark:hover:text-jnanakshi-400 px-2 lg:px-3 py-2 rounded-md text-sm lg:text-base font-medium transition ${
                  isActive ? "text-jnanakshi-500 dark:text-jnanakshi-400" : ""
                }`
              }
            >
              Home
            </NavLink>
            <NavLink
              to="/services"
              className={({ isActive }) =>
                `text-gray-700 dark:text-gray-300 hover:text-jnanakshi-500 dark:hover:text-jnanakshi-400 px-2 lg:px-3 py-2 rounded-md text-sm lg:text-base font-medium transition ${
                  isActive ? "text-jnanakshi-500 dark:text-jnanakshi-400" : ""
                }`
              }
            >
              Services
            </NavLink>
            <NavLink
              to="/about-us"
              className={({ isActive }) =>
                `text-gray-700 dark:text-gray-300 hover:text-jnanakshi-500 dark:hover:text-jnanakshi-400 px-2 lg:px-3 py-2 rounded-md text-sm lg:text-base font-medium transition ${
                  isActive ? "text-jnanakshi-500 dark:text-jnanakshi-400" : ""
                }`
              }
            >
              About Us
            </NavLink>
            <NavLink
              to="/contact-us"
              className={({ isActive }) =>
                `text-gray-700 dark:text-gray-300 hover:text-jnanakshi-500 dark:hover:text-jnanakshi-400 px-2 lg:px-3 py-2 rounded-md text-sm lg:text-base font-medium transition ${
                  isActive ? "text-jnanakshi-500 dark:text-jnanakshi-400" : ""
                }`
              }
            >
              Contact Us
            </NavLink>
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg bg-gray-100 dark:bg-dark-700 hover:bg-gray-200 dark:hover:bg-dark-600 transition-colors duration-300"
              aria-label="Toggle theme"
            >
              {isDark ? (
                <svg className="w-4 h-4 lg:w-5 lg:h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="w-4 h-4 lg:w-5 lg:h-5 text-gray-700" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                </svg>
              )}
            </button>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center space-x-1 sm:space-x-2">
            {/* Mobile Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-1.5 sm:p-2 rounded-lg bg-gray-100 dark:bg-dark-700 hover:bg-gray-200 dark:hover:bg-dark-600 transition-colors duration-300"
              aria-label="Toggle theme"
            >
              {isDark ? (
                <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-700" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                </svg>
              )}
            </button>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-700 dark:text-gray-300 hover:text-jnanakshi-500 dark:hover:text-jnanakshi-400 focus:outline-none focus:text-jnanakshi-500 dark:focus:text-jnanakshi-400 p-1"
            >
              <svg className="h-5 w-5 sm:h-6 sm:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {isOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white dark:bg-dark-800 border-t border-gray-200 dark:border-dark-700">
              <NavLink
                to="/"
                className={({ isActive }) =>
                  `block px-3 py-3 rounded-md text-base font-medium transition ${
                    isActive ? "text-jnanakshi-500 dark:text-jnanakshi-400 bg-jnanakshi-50 dark:bg-dark-700" : "text-gray-700 dark:text-gray-300 hover:text-jnanakshi-500 dark:hover:text-jnanakshi-400 hover:bg-jnanakshi-50 dark:hover:bg-dark-700"
                  }`
                }
                onClick={() => setIsOpen(false)}
              >
                Home
              </NavLink>
              <NavLink
                to="/services"
                className={({ isActive }) =>
                  `block px-3 py-3 rounded-md text-base font-medium transition ${
                    isActive ? "text-jnanakshi-500 dark:text-jnanakshi-400 bg-jnanakshi-50 dark:bg-dark-700" : "text-gray-700 dark:text-gray-300 hover:text-jnanakshi-500 dark:hover:text-jnanakshi-400 hover:bg-jnanakshi-50 dark:hover:bg-dark-700"
                  }`
                }
                onClick={() => setIsOpen(false)}
              >
                Services
              </NavLink>
              <NavLink
                to="/about-us"
                className={({ isActive }) =>
                  `block px-3 py-3 rounded-md text-base font-medium transition ${
                    isActive ? "text-jnanakshi-500 dark:text-jnanakshi-400 bg-jnanakshi-50 dark:bg-dark-700" : "text-gray-700 dark:text-gray-300 hover:text-jnanakshi-500 dark:hover:text-jnanakshi-400 hover:bg-jnanakshi-50 dark:hover:bg-dark-700"
                  }`
                }
                onClick={() => setIsOpen(false)}
              >
                About Us
              </NavLink>
              <NavLink
                to="/contact-us"
                className={({ isActive }) =>
                  `block px-3 py-3 rounded-md text-base font-medium transition ${
                    isActive ? "text-jnanakshi-500 dark:text-jnanakshi-400 bg-jnanakshi-50 dark:bg-dark-700" : "text-gray-700 dark:text-gray-300 hover:text-jnanakshi-500 dark:hover:text-jnanakshi-400 hover:bg-jnanakshi-50 dark:hover:bg-dark-700"
                  }`
                }
                onClick={() => setIsOpen(false)}
              >
                Contact Us
              </NavLink>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
